import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { v4 as uuid } from "uuid";

const ReservationSelector = ({
  selectedDate,
  onSelectionChange,
  initialAtelierId = "",
  initialParticipants = 1,
  initialCreneauId = null,
  initialCreneau = null,
  initialDate = null,
  preserveInitialSelection = false,
}) => {
  // --- Sélections locales
  const [atelierId, setAtelierId] = useState("");
  const [participants, setParticipants] = useState(1);
  const [creneau, setCreneau] = useState(null);
  const [creneauIdSelectionne, setCreneauIdSelectionne] = useState(null);

  const ateliers = useSelector((state) => state.reservation.ateliers);
  const disponibiliteJour = useSelector((state) => state.reservation.creneauxJour) || [];

  const [dynamicMax, setDynamicMax] = useState(8);
  const placesMaxGlobal = useSelector((state) => state.placesMax) || 8;

  const getCreneauId = (item) =>
    item?.creneau_id ?? item?.creneauId ?? item?.id ?? item?._id ?? item?.heure ?? null;

  const selectedAtelier = useMemo(() => {
    if (!atelierId) return null;
    return (
      ateliers.find(
        (a) => String(a.atelier_id ?? a.id) === String(atelierId)
      ) || null
    );
  }, [atelierId, ateliers]);

  const atelierNom = selectedAtelier?.nom || "";

  /* Remonter les infos au parent */
  useEffect(() => {
    if (!onSelectionChange) return;
    onSelectionChange({
      date: selectedDate,
      atelierId,
      atelierNom,
      participants,
      creneau,
      creneauId: getCreneauId(creneau),
    });
  }, [selectedDate, atelierId, atelierNom, participants, creneau, onSelectionChange]);

  useEffect(() => {
    setAtelierId(initialAtelierId || "");
  }, [initialAtelierId]);

  useEffect(() => {
    const parsed = Number(initialParticipants);
    setParticipants(Number.isFinite(parsed) && parsed > 0 ? parsed : 1);
  }, [initialParticipants]);

  useEffect(() => {
    if (!initialCreneauId) return;
    setCreneauIdSelectionne(initialCreneauId);
    if (initialCreneau) {
      setCreneau(initialCreneau);
    }
  }, [initialCreneauId, initialCreneau]);

  useEffect(() => {
    if (!initialCreneauId) return;
    const match = disponibiliteJour.find(
      (item) => getCreneauId(item) === initialCreneauId
    );
    if (match) {
      setCreneau(match);
      setCreneauIdSelectionne(initialCreneauId);
    }
  }, [initialCreneauId, disponibiliteJour]);

  // Reset sélection quand la date ou l'atelier change
  const skipResetRef = React.useRef(preserveInitialSelection);

  useEffect(() => {
    if (skipResetRef.current) {
      skipResetRef.current = false;
      return;
    }
    setCreneau(null);
    setCreneauIdSelectionne(null);
  }, [selectedDate, atelierId]);

  useEffect(() => {
    skipResetRef.current = preserveInitialSelection;
  }, [preserveInitialSelection, initialCreneauId, initialCreneau]);
  const selectedDateObj = selectedDate
    ? new Date(selectedDate)
    : initialDate
    ? new Date(initialDate)
    : null;
  const dateAffichee = selectedDateObj
    ? selectedDateObj.toLocaleDateString("fr-FR", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Aucune date choisie";
  const jourSelectionne = selectedDateObj
    ? selectedDateObj.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
      })
    : null;

  const normalizePlacesValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const normalizeTimeValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number" && Number.isFinite(value)) {
      return `${String(value).padStart(2, "0")}:00:00`;
    }
    if (typeof value === "string") {
      if (/^\d{2}:\d{2}:\d{2}$/.test(value)) return value;
      if (/^\d{2}:\d{2}$/.test(value)) return `${value}:00`;
      if (/^\d{1,2}$/.test(value)) return `${value.padStart(2, "0")}:00:00`;
    }
    return null;
  };

  const addHoursToTime = (timeStr, hoursToAdd = 0) => {
    if (!timeStr) return null;
    const base = new Date(`1970-01-01T${timeStr}`);
    if (Number.isNaN(base.getTime())) return null;
    base.setHours(base.getHours() + Number(hoursToAdd || 0));
    return base.toTimeString().slice(0, 8);
  };

  const formatTimeLabel = (timeStr) => {
    if (!timeStr) return null;
    const [hour = "00", minute = "00"] = timeStr.split(":");
    return `${hour}h${minute}`;
  };

  const parseHeure = (value) => {
    if (value == null) return 0;
    if (typeof value === "string") {
      const [h] = value.split(":");
      return Number(h) || 0;
    }
    return Number(value) || 0;
  };

  const getHeureDebutSource = (item) =>
    item?.creneau_heure_debut ?? item?.heure_debut ?? item?.heure ?? null;

  const getDateKey = (value) => {
    if (!value) return "";
    const dateObj = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateObj.getTime())) return "";
    return dateObj.toISOString().slice(0, 10);
  };

  const selectedDateKey = getDateKey(selectedDateObj);

  useEffect(() => {
    if (creneau) {
      const availablePlaces = normalizePlacesValue(creneau.places);
      if (availablePlaces > 0) {
        setDynamicMax(Math.max(1, Math.min(placesMaxGlobal, availablePlaces)));
        return;
      }
    }
    setDynamicMax(placesMaxGlobal);
  }, [creneau, placesMaxGlobal]);

  const creneauxDisponibles = useMemo(() => {
    if (!Array.isArray(disponibiliteJour)) return [];

    const grouped = new Map();

    disponibiliteJour.forEach((dispo) => {
      if (!dispo) return;
      const placesValue = normalizePlacesValue(dispo.places);
      if (placesValue <= 0) return;

      const heureSource = getHeureDebutSource(dispo);
      const heureNormalisee = normalizeTimeValue(heureSource);
      const dateKey = getDateKey(dispo.date) || selectedDateKey;
      const aggregationKey = `${dateKey}|${heureNormalisee || heureSource || ""}`;
      if (!aggregationKey.trim()) return;

      if (grouped.has(aggregationKey)) {
        const current = grouped.get(aggregationKey);
        grouped.set(aggregationKey, {
          ...current,
          places: normalizePlacesValue(current.places) + placesValue,
        });
      } else {
        grouped.set(aggregationKey, {
          ...dispo,
          places: placesValue,
        });
      }
    });

    return Array.from(grouped.values()).sort(
      (a, b) => parseHeure(getHeureDebutSource(a)) - parseHeure(getHeureDebutSource(b))
    );
  }, [disponibiliteJour, selectedDateKey]);

  const peutChoisirCreneau = Boolean(atelierId);

  return (
    <div className="w-full max-w-xl mx-auto bg-white shadow-md rounded-xl p-6 space-y-6">
      {/* Date */}
      <div className="text-center text-lg font-semibold text-gray-700">
        Date sélectionnée :{" "}
        <span className="text-blue-600">{dateAffichee}</span>
      </div>
      {/* Atelier */}
      <div className="relative border-b border-gray-300">
        <select
          value={atelierId}
          onChange={(e) => setAtelierId(e.target.value)}
          className="w-full appearance-none bg-transparent py-2 text-gray-800 focus:outline-none"
        >
          <option value="">Choisir un atelier...</option>
          {ateliers.filter((a) => a.actif !== false).map((a) => (
            <option key={a.atelier_id ?? a.id} value={a.atelier_id ?? a.id}>
              {a.nom}
            </option>
          ))}
        </select>
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
          ▼
        </span>
      </div>

      {/* Nombre de participants */}
      <div className="flex justify-between items-center">
        <label className="text-gray-700 font-medium">
          Nombre de participants :
        </label>
        <input
          type="number"
          min="1"
          max={dynamicMax}
          value={participants}
          onChange={(e) => setParticipants(Number(e.target.value))}
          className="w-20 border-b border-gray-300 text-center focus:outline-none focus:border-blue-500"
        />
        <span className="text-gray-500 text-sm">
          (max {dynamicMax})
        </span>
      </div>
      <p className="text-sm text-gray-600 italic">Si vous souhaitez venir à plusieurs, merci de ne faire qu'une seule réservation. </p>
      {/* Créneaux disponibles */}
      <div className="space-y-2">
        <h4 className="text-gray-700 font-semibold mb-2">
          Créneaux disponibles :
        </h4>
        {!peutChoisirCreneau && (
          <p className="text-orange-600 text-sm">
            Sélectionnez d’abord un atelier pour afficher les créneaux disponibles.
          </p>
        )}
        {peutChoisirCreneau && creneauxDisponibles.length > 0 ? (
          <div className="space-y-2 max-h-[25vh] overflow-y-auto pr-1 scr">
            {creneauxDisponibles.map((c) => {
              const itemId = getCreneauId(c) ?? uuid();
              const isSelected = itemId === creneauIdSelectionne;
              const heureDebutRaw =
                c.creneau_heure_debut ?? c.heure_debut ?? c.heure ?? null;
              const heureFinRaw =
                c.creneau_heure_fin ?? c.heure_fin ?? c.fin ?? null;
              const heureDebutFull = normalizeTimeValue(heureDebutRaw);
              let heureFinFull = normalizeTimeValue(heureFinRaw);
              if (!heureFinFull && selectedAtelier?.duree) {
                heureFinFull = addHoursToTime(
                  heureDebutFull,
                  selectedAtelier.duree
                );
              }
              const labelHeureDebut = formatTimeLabel(heureDebutFull) || "--";
              const labelHeureFin = formatTimeLabel(heureFinFull);
              return (
              <div
                key={itemId}
                onClick={() => {
                  if (!peutChoisirCreneau) return;
                  setCreneau(c);
                  setCreneauIdSelectionne(itemId);
                }}
                className={`px-4 py-2 rounded-lg border transition-all ${
                  isSelected ? "border-orange-600 bg-orange-50" : "border-gray-200 hover:border-orange-200"
                } ${peutChoisirCreneau ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}
              >
                <span>
                  {labelHeureFin
                    ? `De ${labelHeureDebut} à ${labelHeureFin} — `
                    : `À ${labelHeureDebut} — `}
                </span>
                <span className="text-gray-600">
                  {c.places} places disponibles
                </span>
              </div>
            )})}
          </div>
        ) : peutChoisirCreneau ? (
          <p className="text-gray-500 italic">
            Aucun créneau disponible.
          </p>
        ) : null}
      </div>
      {/*creneau && (
        <div className="mt-4 p-3 border border-orange-200 rounded-lg bg-orange-50 text-orange-700">
          Créneau sélectionné :{" "}
          <span className="font-semibold">
            {jourSelectionne ? `${jourSelectionne} - ` : ""}
            {String(creneau.heure).padStart(2, "0")}h00
          </span>{" "}
        </div>
      )*/}
    </div>
  );
};

export default ReservationSelector;
